import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import "./class.public.css";

/** Props for {@link ClassLandingNameDialog}. */
interface ClassLandingNameDialogProps {
  /** Whether the dialog is shown. */
  open: boolean;
  /** Disables the buttons (and closing) while registration or enroll is in flight. */
  submitting: boolean;
  /** Closes the dialog without registering. */
  onCancel: () => void;
  /** Called with the trimmed name; the parent registers, then enrols. */
  onConfirm: (name: string) => void;
}

/**
 * Asks for the student's name when check-student says registration is required
 * (no Self-Serve student exists for a bookstore access code yet).
 * Returning students never see it. API errors from register or enroll show in the page's
 * alert box and the dialog stays open.
 */
const ClassLandingNameDialog: React.FC<ClassLandingNameDialogProps> = ({
  open,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  // Start with an empty field and no error each time the dialog opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    setName("");
    setError("");
    const onHistory = (event: Event) => {
      const inputEvent = event as InputEvent;
      if (inputEvent.inputType !== "historyUndo" && inputEvent.inputType !== "historyRedo") {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.id !== "ss-redeem-code" && target?.id !== "ss-redeem-email") {
        return;
      }
      // Undo continues into the fields behind this dialog once the name field has no history.
      event.preventDefault();
    };
    document.addEventListener("beforeinput", onHistory, true);
    return () => document.removeEventListener("beforeinput", onHistory, true);
  }, [open]);

  /** Requires a non-blank name, then passes it to `onConfirm`. */
  const handleConfirm = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setError("");
    onConfirm(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      aria-labelledby="ss-name-dialog-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: "ss-public-dialog" }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleConfirm();
        }}
      >
        <DialogTitle id="ss-name-dialog-title">Enter your name</DialogTitle>
        <DialogContent>
          <p className="ss-public-dialog-copy">
            Please enter the name you want associated with this class.
          </p>
          <label className="required" htmlFor="ss-redeem-name">Full name</label>
          <input
            id="ss-redeem-name"
            type="text"
            maxLength={500}
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={name}
            disabled={submitting}
            autoComplete="name"
            aria-required="true"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "ss-redeem-name-error" : undefined}
            placeholder="Your full name"
            onChange={(event) => setName(event.target.value)}
          />
          {error ? (
            <div id="ss-redeem-name-error" className="invalid-feedback d-block">
              {error}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            className="ss-public-dialog-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="ss-public-dialog-confirm"
            variant="contained"
            disableElevation
            disabled={submitting}
          >
            {submitting ? "Checking…" : "Continue"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClassLandingNameDialog;
